import { CacheService } from 'core/cache/services/CacheService';
import { FunnelDtoMapper } from 'core/funnel/mappers';
import { FunnelRequestFilters, LightFunnel, LightFunnelRequestFilters } from '../../entities';
import { FunnelService } from '../../interfaces';
import { FunnelServiceDecorator } from './FunnelServiceDecorator';
import { FunnelDto } from './dto';

export class FunnelServiceWithCaching extends FunnelServiceDecorator {
  private static pendingLoads = new Map<string, Promise<Array<FunnelDto> | null>>();
  private static pendingLightLoads = new Map<string, Promise<Array<LightFunnel> | null>>();

  constructor(
    private readonly cachingProvider: CacheService,
    funnelService: FunnelService,
    private readonly appName: string,
  ) {
    super(funnelService);
  }

  async getFunnels(params: FunnelRequestFilters) {
    const language = params.language ? `-${params.language}` : '';
    const cacheKey = `funnels-${this.appName}-${params.funnelLink}${language}`;

    const isLoadPending = FunnelServiceWithCaching.pendingLoads.has(cacheKey);

    if (!isLoadPending) {
      const load = this.loadRawFunnels(cacheKey, params).finally(() =>
        FunnelServiceWithCaching.pendingLoads.delete(cacheKey),
      );

      FunnelServiceWithCaching.pendingLoads.set(cacheKey, load);
    }

    const rawFunnels = await FunnelServiceWithCaching.pendingLoads.get(cacheKey);

    if (!rawFunnels) {
      return null;
    }

    const dtoMapper = new FunnelDtoMapper();
    const funnels = rawFunnels.map(dtoMapper.toEntity);

    return funnels;
  }

  async getLightFunnels(filters: LightFunnelRequestFilters) {
    const { funnelLink } = filters;

    const isLoadPending = FunnelServiceWithCaching.pendingLightLoads.has(funnelLink);

    if (!isLoadPending) {
      const load = super
        .getLightFunnels(filters)
        .finally(() => FunnelServiceWithCaching.pendingLightLoads.delete(funnelLink));

      FunnelServiceWithCaching.pendingLightLoads.set(funnelLink, load);
    }

    const lightFunnels = await FunnelServiceWithCaching.pendingLightLoads.get(funnelLink);

    return lightFunnels ?? null;
  }

  private async loadRawFunnels(cacheKey: string, params: FunnelRequestFilters) {
    const cachedRawFunnels = await this.cachingProvider.get<Array<FunnelDto>>(cacheKey);

    if (cachedRawFunnels) {
      return cachedRawFunnels;
    }

    const rawFunnels = await super.getFunnelsRawResponse(params);

    if (!rawFunnels) {
      return null;
    }

    await this.cachingProvider.put(rawFunnels, cacheKey);

    return rawFunnels;
  }
}
